// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React from "react";
import { Link, Location } from "@reach/router";
import { Breadcrumb } from "antd";
import { useTranslation } from "react-i18next";
import { HomeOutlined } from "@ant-design/icons";

interface BreadcrumbItem {
  label?: string;
  icon?: JSX.Element;
  url: string | null;
}

interface BreadcrumbRule {
  regex: RegExp;
  items: BreadcrumbItem[] | null;
}

interface BreadcrumbRuleResult {
  breadcrumbRule: BreadcrumbRule;
  result: RegExpExecArray | null;
}

const Breadcrumbs: React.FunctionComponent = () => {
  const { t } = useTranslation();

  const breadcrumbItems: { [id: string]: BreadcrumbItem } = {
    home: {
      icon: <HomeOutlined />,
      url: "/"
    },
    news: {
      label: t("breadcrumb.news"),
      url: "/news"
    },
    newsThemes: {
      label: t("breadcrumb.news_themes"),
      url: "/newsThemes"
    },
    settings: {
      label: t("breadcrumb.settings"),
      url: "/settings"
    }
  };

  const breadcrumbRules: BreadcrumbRule[] = [
    {
      regex: /\/news\/addNews/g,
      items: [
        breadcrumbItems.home,
        breadcrumbItems.news,
        { label: t("breadcrumb.add_news"), url: null }
      ]
    },
    {
      regex: /\/news\/[a-zA-Z0-9_]+/g,
      items: [
        breadcrumbItems.home,
        breadcrumbItems.news,
        { label: t("breadcrumb.detail_news"), url: null }
      ]
    },
    {
      regex: /\/newsThemes\/add/g,
      items: [
        breadcrumbItems.home,
        breadcrumbItems.newsThemes,
        { label: t("breadcrumb.add_news_theme"), url: null }
      ]
    },
    {
      regex: /\/newsThemes\/[a-zA-Z0-9_]+/g,
      items: [
        breadcrumbItems.home,
        breadcrumbItems.newsThemes,
        { label: t("breadcrumb.detail_news_theme"), url: null }
      ]
    },
    {
      regex: /\/newsThemes/g,
      items: [
        breadcrumbItems.home,
        { ...breadcrumbItems.newsThemes, url: null }
      ]
    },
    {
      regex: /\/news/g,
      items: [breadcrumbItems.home, { ...breadcrumbItems.news, url: null }]
    },
    {
      regex: /\/settings/g,
      items: [breadcrumbItems.home, { ...breadcrumbItems.settings, url: null }]
    } /*,
    {
      regex: /\//g,
      items: [{ ...breadcrumbItems.home, url: null }]
    }*/
  ];

  return (
    <Location>
      {({ location }) => {
        const ruleResult: BreadcrumbRuleResult | undefined = breadcrumbRules
          .map((breadcrumbRule: BreadcrumbRule) => {
            const result = breadcrumbRule.regex.exec(location.pathname);
            // Reset regex for next use
            breadcrumbRule.regex.lastIndex = 0;
            return { breadcrumbRule, result };
          })
          .find(ruleResult => ruleResult !== null && ruleResult.result != null);
        if (
          ruleResult !== undefined &&
          ruleResult.breadcrumbRule.items !== null
        ) {
          return (
            <Breadcrumb className="content-breadcrumb">
              {ruleResult.breadcrumbRule.items.map((item: BreadcrumbItem) => {
                const { url, label, icon } = item;
                const parsedUrl: string | null =
                  url && ruleResult.result && ruleResult.result?.length > 1
                    ? url.replace("$1", ruleResult.result[1])
                    : url;
                return (
                  <Breadcrumb.Item key={parsedUrl || label}>
                    {parsedUrl ? (
                      <Link to={parsedUrl}>
                        {icon && <span>{icon}</span>}
                        {label && <span>{label}</span>}
                      </Link>
                    ) : (
                      <>
                        {icon && <span>{icon}</span>}
                        {label && <span>{label}</span>}
                      </>
                    )}
                  </Breadcrumb.Item>
                );
              })}
            </Breadcrumb>
          );
        } else {
          return null;
        }
      }}
    </Location>
  );
};

export default Breadcrumbs;
